import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { BtmReturnInspectionProduct } from "../model/btm-return-inspection-product";
import { classNamesList } from "../../../../common/utils/class-name";
import { t } from "i18next";

import "./style.scss";

interface IBtmRequestInspectionGalleryProps {
    item: BtmReturnInspectionProduct;
    onClose: () => void;
}

export const BtmRequestInspectionGallery = (props: IBtmRequestInspectionGalleryProps) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [enableImageNavigation, setEnableImageNavigation] = useState(false);

    const selectedThumbnailRef = useRef<HTMLButtonElement>(null);
    const thumbnailObserverRef = useRef<ResizeObserver | null>(null);

    const imagesList = props.item.files ?? [];
    const selectedImage = useMemo(() => imagesList[selectedImageIndex],
        [imagesList, selectedImageIndex]);

    useEffect(() => {
        setSelectedImageIndex(0);
    }, [props.item]);

    useEffect(() => {
        if (imagesList.length === 0) {
            props.onClose();
        }
    }, [imagesList.length, props.onClose]);

    useEffect(() => {
        selectedThumbnailRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, [selectedImageIndex, imagesList]);

    const thumbnailsRef = useCallback((thumbnails: HTMLDivElement | null) => {
        thumbnailObserverRef.current?.disconnect();
        thumbnailObserverRef.current = null;

        if (!thumbnails) {
            setEnableImageNavigation(false);
            return;
        }

        const updateOverflow = () =>
            setEnableImageNavigation(thumbnails.scrollHeight > thumbnails.clientHeight);
        const observer = new ResizeObserver(updateOverflow);
        thumbnailObserverRef.current = observer;
        observer.observe(thumbnails);
        Array.from(thumbnails.children).forEach(thumbnail => observer.observe(thumbnail));
        updateOverflow();

    }, [imagesList]);

    return <Dialog open fullWidth classes={{ paper: "btm-request-gallery" }} onClose={props.onClose} maxWidth={"lg"}>
        <DialogTitle>
            {t("components.blueBrandReturns.page.photos.title", {
                productName: props.item.productName,
            })}
        </DialogTitle>
        <DialogContent className={"btm-request-gallery__content"}>
            {selectedImage && <div className={classNamesList(["btm-request-gallery__photo",
                imagesList.length === 1 ? "btm-request-gallery__photo--full-width" : ""])}>
                <img className={"btm-request-gallery__photo-image"}
                     src={`data:${selectedImage.mimeType};base64,${selectedImage.content}`} />
                {imagesList.length > 1 && <div className={"btm-request-gallery__slider"}>
                    {enableImageNavigation && <IconButton className={"btm-request-gallery__navigation btm-request-gallery__navigation--previous"}
                                                          disabled={selectedImageIndex === 0}
                                                          onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}>
                        <KeyboardArrowUp />
                    </IconButton>}
                    <div ref={thumbnailsRef} className={"btm-request-gallery__thumbnails"}>
                        {imagesList.map((file, index) =>
                            <button key={`thumbnail-${index}`}
                                    type={"button"}
                                    ref={selectedImageIndex === index ? selectedThumbnailRef : undefined}
                                    className={classNamesList(["btm-request-gallery__thumbnail",
                                        selectedImageIndex === index ? "btm-request-gallery__thumbnail--selected" : ""])}
                                    aria-pressed={selectedImageIndex === index}
                                    onClick={() => setSelectedImageIndex(index)}>
                                <img src={`data:${file.mimeType};base64,${file.content}`} />
                            </button>)}
                    </div>
                    {enableImageNavigation && <IconButton className={"btm-request-gallery__navigation btm-request-gallery__navigation--next"}
                                                          disabled={selectedImageIndex === imagesList.length - 1}
                                                          onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}>
                        <KeyboardArrowDown />
                    </IconButton>}
                </div>}
            </div>}
        </DialogContent>
    </Dialog>;
};
